import React, { useState, useEffect } from 'react';
import { get, post, put } from '../../services/apiController';
import { toast, ToastContainer } from 'react-toastify';
import { DEBOUNCE_DELAY, monthsDropdown, TABLE_DEFAULT_LIMIT } from '../../util/constant';
import { Table, Modal, Button, Form, Col, Row, FloatingLabel } from 'react-bootstrap';
import PaginationInPage from '../Pagination';
import { dateFormat } from '../../util/helper';
import { EditIcon } from '../icons';
import moment from 'moment';
import Select from 'react-select';
import useDebounce from '../useDebounce';
import FactoryDropdown from './FactoryDropdown';
import { saveAs } from 'file-saver';
import { GrDocumentPdf } from 'react-icons/gr';
const years = Array.from({ length: 4 }, (_, i) => {
  const year = 2024 + i;
  return { value: year, label: year.toString() };
});

const LogSheet = () => {
  const [logs, setLogs] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [runningVehicleList, setRunningVehicleList] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    runningVehicleId: '',
  });
  const formFields = {
    id: null,
    runningVehicleId: selectedVehicle ? selectedVehicle?.value : '',
    driverName: '',
    date: moment().format('YYYY-MM-DD'),
    startingKms: '',
    endingKms: '',
    dieselInLtrs: '',
    amount: '',
  };
  const [formData, setFormData] = useState(formFields);

  const fetchLogEntries = (downloadExcel) => {
    if (filters.runningVehicleId) {
      const { runningVehicleId, month, year } = filters;
      let url = `/vm/diesel-log-entries/?runningVehicleId=${runningVehicleId}&month=${month}&year=${year}`;
      if (downloadExcel) {
        url += `&downloadExcel=true`;
      } else {
        url += `&page=${currentPage}&limit=${TABLE_DEFAULT_LIMIT}`;
      }
      get(url, { responseType: downloadExcel ? 'arraybuffer' : 'json' })
        .then((response) => {
          if (downloadExcel) {
            const { month, year } = filters;
            const fileName = `Vehicle Log Report - ${monthsDropdown.find((m) => m.value === month)?.label} ${year}.pdf`;
            const blob = new Blob([response], { type: 'application/pdf' });
            saveAs(blob, fileName);
          } else {
            setLogs(response?.data?.rows);
            setTotalRecords(response?.data?.count);
          }
        })
        .catch((err) => {
          toast.error(err?.response?.data?.message || err?.message);
        });
    }
  };

  async function getRunningVehicle() {
    try {
      get(`/vm/running-vehicle`).then((response) => {
        setRunningVehicleList(response?.data?.rows);
      });
    } catch (err) {
      toast.error(err.response.data.data.message);
    }
  }

  useEffect(() => {
    fetchLogEntries();
  }, [currentPage]);

  useEffect(() => {
    getRunningVehicle();
  }, []);

  useEffect(() => {
    if (formData.runningVehicleId) {
      const runningVehicle = runningVehicleList.find((item) => +item.id === +formData.runningVehicleId);
      if (runningVehicle) {
        setSelectedVehicle({
          value: runningVehicle?.id,
          label: `${runningVehicle?.vehicleModelName} (${runningVehicle?.registrationNumber})`,
        });
      }
    }
  }, [formData.runningVehicleId, runningVehicleList]);

  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    // If the runningVehicleId is selected, fetch the last entry for that vehicle
    if (name === 'runningVehicleId') {
      try {
        const response = await get(`/vm/diesel-log-entries/last-log/${value}`);
        const lastLog = response?.data;
        setFormData({
          ...formData,
          [name]: value,
          startingKms: lastLog?.endingKms || '', // Set startingKms based on the last log's endingKms
        });
      } catch (err) {
        console.error('Failed to fetch the last log entry for the selected vehicle.');
        setFormData({
          ...formData,
          [name]: value,
          startingKms: '', // Reset startingKms if no data is available
        });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSave = async (e) => {
    try {
      e.preventDefault();
      setIsSubmitting(true);
      if (formData.id) {
        await put(formData, `/vm/diesel-log-entries/${formData.id}`);
        toast.success('Log entry updated successfully!');
      } else {
        await post(formData, `/vm/diesel-log-entries`);
        toast.success('Log entry created successfully!');
      }
      setShowModal(false);
      setFormData(formFields);
      fetchLogEntries();
    } catch (err) {
      toast.error(err?.response?.data?.data?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (log) => {
    setFormData(log);
    setShowModal(true);
  };

  const handleAddNew = () => {
    if (selectedVehicle && selectedVehicle.value) {
      const e = { target: { name: 'runningVehicleId', value: selectedVehicle.value } };
      handleInputChange(e);
    }
    setFormData(formFields);
    setShowModal(true);
  };

  useDebounce(
    () => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchLogEntries();
      }
    },
    [filters],
    DEBOUNCE_DELAY
  );

  return (
    <div>
      <ToastContainer />
      <div className="card">
        <div className="card-header white-color" style={{ padding: '1rem 1.7rem' }}>
          <h4 className="card-title mb-0" style={{ fontSize: '16px' }}>
            Logs | {totalRecords ?? 0}
          </h4>

          <div className="d-flex align-items-center gap-3">
            <div className="form-floating text-start">
              <Select
                className="input"
                value={monthsDropdown.find((m) => m.value === filters.month)}
                options={monthsDropdown}
                onChange={(selectedOption) => setFilters((prev) => ({ ...prev, month: selectedOption?.value }))}
                placeholder="Select Month"
                isClearable
                isSearchable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '55px',
                    minWidth: '200px',
                  }),
                }}
              />
            </div>
            <div className="form-floating text-start">
              <Select
                className="input"
                value={years.find((y) => y.value === filters.year)}
                options={years}
                onChange={(selectedOption) => setFilters((prev) => ({ ...prev, year: selectedOption?.value }))}
                placeholder="Select Year"
                isClearable
                isSearchable
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '55px',
                    minWidth: '200px',
                  }),
                }}
              />
            </div>
            <FactoryDropdown
              withFloatingLable={true}
              onFactoryChange={() => {
                setSelectedVehicle(null);
                fetchLogEntries();
                getRunningVehicle();
              }}
            />
            <div className="form-floating text-start">
              <Select
                className="input"
                value={selectedVehicle}
                options={runningVehicleList.map((item) => ({
                  value: item?.id,
                  label: `${item?.vehicleModelName} (${item?.registrationNumber})`,
                }))}
                onChange={(selectedOption) => {
                  setSelectedVehicle({ value: selectedOption?.value, label: selectedOption?.label });
                  setFilters((prev) => ({ ...prev, runningVehicleId: selectedOption?.value || '' }));
                }}
                placeholder="Select Vehicle"
                isClearable
                isSearchable
                styles={{
                  control: (provided, state) => ({
                    ...provided,
                    minHeight: '55px', // Adjust the height value as needed
                    minWidth: '200px',
                  }),
                }}
              />
            </div>
            {localStorage.getItem('factoryId') && selectedVehicle && (
              <>
                <GrDocumentPdf
                  style={{ cursor: 'pointer', width: '2rem', height: '2rem' }}
                  onClick={() => fetchLogEntries(true)}
                  className="react-icons red"
                  title="download pdf"
                />
                <Button className="btn app-btn-default dark-color" onClick={handleAddNew}>
                  Create Log
                </Button>
              </>
            )}
          </div>
        </div>
        <div className="card-body px-0 vehicle-requests-table pt-0 pb-0">
          <Table responsive striped>
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Date</th>
                <th>Registration Number</th>
                <th>Driver Name</th>
                <th>Starting Kms</th>
                <th>Ending Kms</th>
                <th>Total Kms</th>
                <th>Diesel in Ltrs</th>
                <th>Average Mileage</th>
                <th>Total Amount</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {Array.isArray(logs) && logs.length > 0 ? (
                logs?.map((item, index) => (
                  <tr key={item?.id}>
                    <td>{(currentPage - 1) * TABLE_DEFAULT_LIMIT + (index + 1)}</td>
                    <td>{dateFormat(item?.date)}</td>
                    <td>{item?.OnroadVehicle?.registrationNumber || '-'}</td>
                    <td>{item?.driverName ?? '-'}</td>
                    <td>{item?.startingKms ? Number(item.startingKms).toFixed(2) : '-'}</td>
                    <td>{item?.endingKms ? Number(item.endingKms).toFixed(2) : '-'}</td>
                    <td>{item?.endingKms && item?.startingKms ? (item.endingKms - item.startingKms).toFixed(2) : '-'}</td>
                    <td>{item?.dieselInLtrs ? Number(item.dieselInLtrs).toFixed(2) : '-'}</td>
                    <td>
                      {item?.endingKms && item?.startingKms && item?.dieselInLtrs
                        ? ((item.endingKms - item.startingKms) / item.dieselInLtrs).toFixed(2)
                        : '-'}
                    </td>
                    <td>₹ {item?.amount ? Number(item.amount).toFixed(2) : '-'}</td>
                    <td>
                      <span onClick={() => handleEdit(item)} className="edit-icon">
                        <EditIcon style={{ color: 'teal' }} />
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10">
                    {selectedVehicle && localStorage.getItem('factoryId')
                      ? 'No logs found.'
                      : 'Please select factory and vehicle to create & fetch logs.'}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
          <PaginationInPage
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
            totalRecords={totalRecords}
            TABLE_DEFAULT_LIMIT={TABLE_DEFAULT_LIMIT}
          />
        </div>
      </div>

      {/* Modal for Add/Edit */}
      {showModal && (
        <Modal size="lg" show={showModal} backdrop="static" keyboard={false} className="request-modal">
          <Modal.Header>
            <Modal.Title>{formData.id ? 'Edit Log Entry' : 'Add Log Entry'}</Modal.Title>
            <button
              type="button"
              className="btn-close"
              aria-label="Close"
              onClick={() => {
                setFormData(formFields);
                setShowModal(false);
              }}
            ></button>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleSave}>
              <Row>
                <Col xs={6}>
                  <FloatingLabel controlId="floatingInput" label="Date" className="mb-3">
                    <Form.Control
                      required
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      placeholder="Select Date"
                      max={moment().format('YYYY-MM-DD')}
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={6}>
                  <FloatingLabel controlId="floatingInput" label="Driver Name" className="mb-3">
                    <Form.Control
                      required
                      type="text"
                      name="driverName"
                      value={formData.driverName}
                      onChange={handleInputChange}
                      placeholder="Enter Driver Name"
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={12}>
                  <Form.Label className="font-weight-bold">Select Running Vehicle</Form.Label>
                  <Select
                    className="input mb-3"
                    value={selectedVehicle}
                    options={runningVehicleList.map((item) => ({
                      value: item?.id,
                      label: `${item?.vehicleModelName} (${item?.registrationNumber})`,
                    }))}
                    onChange={(selectedOption) => {
                      setSelectedVehicle({ value: selectedVehicle?.value, label: selectedVehicle?.label });
                      handleInputChange({
                        target: { name: 'runningVehicleId', value: selectedOption?.value },
                      });
                    }}
                    placeholder="Select Running Vehicle"
                    isClearable
                    isSearchable
                  />
                </Col>

                <Col xs={4}>
                  <FloatingLabel controlId="floatingInput" label="Starting Kms" className="mb-3">
                    <Form.Control
                      required
                      type="number"
                      name="startingKms"
                      value={formData.startingKms}
                      onChange={handleInputChange}
                      placeholder="Enter Starting Kms"
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={4}>
                  <FloatingLabel controlId="floatingInput" label="Ending Kms" className="mb-3">
                    <Form.Control
                      required
                      type="number"
                      name="endingKms"
                      value={formData.endingKms}
                      onChange={handleInputChange}
                      placeholder="Enter Ending Kms"
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={4}>
                  <FloatingLabel controlId="floatingInput" label="Total Kms" className="mb-3">
                    <Form.Control
                      disabled
                      type="number"
                      value={formData.endingKms && formData.startingKms ?
                        (parseFloat(formData.endingKms) - parseFloat(formData.startingKms)).toFixed(2) : ''}
                      placeholder="Total Kilometers"
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={6}>
                  <FloatingLabel controlId="floatingInput" label="Diesel in Liters" className="mb-3">
                    <Form.Control
                      required
                      type="number"
                      name="dieselInLtrs"
                      value={formData.dieselInLtrs}
                      onChange={handleInputChange}
                      placeholder="Enter Diesel Quantity"
                    />
                  </FloatingLabel>
                </Col>
                <Col xs={6}>
                  <FloatingLabel controlId="floatingInput" label="Total Amount" className="mb-3">
                    <Form.Control
                      required
                      type="number"
                      name="amount"
                      value={formData.amount}
                      onChange={handleInputChange}
                      placeholder="Enter Total Amount"
                    />
                  </FloatingLabel>
                </Col>
              </Row>
              <div className="container text-center">
                <Button
                  type="submit"
                  className="add-request-btn mt-4 mb-3"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Submitting...
                    </>
                  ) : (
                    'Submit'
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default LogSheet;
